/**
 * Editor del PERFIL DE PROFESOR (F3-D): bio/especialidades/foto/título/
 * public_slug + toggle is_public, con VISTA PREVIA en vivo de la página pública
 * (reutiliza `PublicTeacherContent`). Muestra la URL pública y enlaza a ella
 * cuando está publicado.
 *
 * **Plan 06 · E4:** la ficha se lee y se escribe por REST
 * (`GET|PUT /api/academy/:orgId/teachers/:userId`, authz «dueño o
 * ManageMembers» — la misma que aplicaba el write-path, movida al único sitio
 * que ahora puede aplicarla). La ficha de un tercero ya no está en el SQLite
 * local, así que el `update … where id` local no encontraba fila y la edición
 * se perdía sin ruido.
 */
import { type Component, createMemo, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { ExternalLink, Eye, Save } from "lucide-solid";
import {
  academyPeopleQueryKeys,
  type PublicTeacherPageDTO,
  slugifyPublicSlug,
  type TeacherRecordPatch,
} from "@space/shared";
import { eq, organization } from "@space/powersync";
import { useI18n } from "@/providers/i18n-provider";
import { useSession } from "@/lib/auth";
import { PageContainer } from "@/components/ui/page-container";
import { PageHeader } from "@/components/ui/page-header";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { showToast } from "@/components/ui/toast";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { createPowerSyncSingle } from "@space/shared/solid";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";
import { useActiveOrg } from "../../hooks/use-active-org";
import { useMyOrgRole, useSaveTeacherRecord, useTeacherRecord } from "../../hooks/use-people-data";
import { useOrgIdentities } from "../../hooks/use-org-identities";
import { PublicTeacherContent } from "./public-teacher-page";

const field =
  "w-full rounded-lg border-2 border-border bg-field-background px-3 py-2 text-sm focus-visible:border-primary focus-visible:outline-none";

export const TeacherProfileEditor: Component<{ userId: string }> = (props) => {
  const { t } = useI18n();
  const { activeOrgId, activeOrg } = useActiveOrg();
  const profile = useTeacherRecord(activeOrgId, () => props.userId);
  const save = useSaveTeacherRecord(activeOrgId, () => props.userId);
  // D3 (plan 06 · E2): identidad desde la fuente única, no desde un join local.
  const people = useOrgIdentities(activeOrgId);
  const identity = () => people.identityOf(props.userId);
  const rbac = useMyOrgRole(activeOrgId);
  const org = useOrganizationRow(activeOrgId);

  // El propio profesor o quien tenga ManageMembers puede editar. Es la MISMA
  // regla que aplica `can_write_teacher_record` en el servidor; aquí es
  // cortesía (no enseñar un formulario que va a dar 403), allí es la autoridad.
  //
  // Antes comparaba `props.userId === identity()?.userId`, que es la identidad
  // DE ESA MISMA FICHA: siempre igual, así que «soy el dueño» daba true para
  // cualquiera. Se compara contra el usuario de la sesión.
  const { userId: sessionUserId } = useSession();
  const canEdit = () => rbac.can("ManageMembers") || props.userId === sessionUserId();

  const p = () => profile.data;
  const orgSlug = () => org.data?.slug ?? "";
  const publicUrl = () =>
    orgSlug() && p()?.publicSlug ? `/teachers/${orgSlug()}/${p()?.publicSlug}` : "";

  // DTO para la vista previa en vivo (compone datos locales).
  const previewPage = createMemo<PublicTeacherPageDTO>(() => ({
    org: {
      name: activeOrg()?.name ?? org.data?.name ?? null,
      slug: orgSlug() || null,
      logo: org.data?.logo ?? null,
      branding: null,
    },
    teacher: {
      name: identity()?.name ?? null,
      title: p()?.title ?? null,
      bio: p()?.bio ?? null,
      specialties: specialtiesOf(p()?.specialties),
      photo: p()?.photo ?? null,
      publicSlug: p()?.publicSlug ?? null,
    },
    courses: [],
  }));

  // El patch es DISPERSO: lo que no se manda no se toca (misma semántica que
  // tenía el `set()` de Drizzle, por eso el formulario no cambia de forma).
  const patch = (input: TeacherRecordPatch) => {
    save.mutate(input);
  };

  // Crear = un PUT vacío: el upsert del servidor materializa la ficha con los
  // valores por defecto de la migración. No hay id que fabricar en el cliente.
  const create = async () => {
    await save.mutateAsync({});
    showToast(t(msg`Teacher profile created`), "success");
  };

  return (
    <PageContainer maxWidth="5xl">
      <div class="space-y-5">
        <PageHeader
          title={t(msg`Teacher profile`)}
          description={t(msg`Public page shown to prospective students.`)}
          backTo="/academy/people"
          icon={Eye}
        />

        <Show when={!profile.isLoading} fallback={<Skeleton class="h-64 w-full rounded-2xl" />}>
          <Show
            when={p()}
            fallback={
              <div class="flex flex-col items-center gap-3 rounded-2xl border-2 border-dashed border-border p-10 text-center">
                <p class="text-sm text-muted-foreground">
                  {t(msg`This member has no teacher profile yet.`)}
                </p>
                <Show when={canEdit()}>
                  <Button data-testid="teacher-create" onClick={() => void create()}>
                    {t(msg`Create teacher profile`)}
                  </Button>
                </Show>
              </div>
            }
          >
            <div class="grid gap-6 lg:grid-cols-2">
              {/* Formulario */}
              <fieldset class="space-y-3" disabled={!canEdit()} data-testid="teacher-editor">
                <label class="block space-y-1 text-xs text-muted-foreground">
                  {t(msg`Title`)}
                  <input
                    class={field}
                    data-testid="teacher-title"
                    value={p()?.title ?? ""}
                    onChange={(e) => patch({ title: e.currentTarget.value || null })}
                  />
                </label>
                <label class="block space-y-1 text-xs text-muted-foreground">
                  {t(msg`Bio`)}
                  <textarea
                    class={`${field} min-h-[120px] resize-y`}
                    data-testid="teacher-bio"
                    value={p()?.bio ?? ""}
                    onChange={(e) => patch({ bio: e.currentTarget.value || null })}
                  />
                </label>
                <label class="block space-y-1 text-xs text-muted-foreground">
                  {t(msg`Specialties (comma-separated)`)}
                  <input
                    class={field}
                    data-testid="teacher-specialties"
                    value={specialtiesOf(p()?.specialties).join(", ")}
                    onChange={(e) =>
                      patch({
                        specialties: e.currentTarget.value
                          .split(",")
                          .map((s) => s.trim())
                          .filter(Boolean),
                      })
                    }
                  />
                </label>
                <label class="block space-y-1 text-xs text-muted-foreground">
                  {t(msg`Photo URL`)}
                  <input
                    class={field}
                    data-testid="teacher-photo"
                    value={p()?.photo ?? ""}
                    onChange={(e) => patch({ photo: e.currentTarget.value || null })}
                  />
                </label>
                <label class="block space-y-1 text-xs text-muted-foreground">
                  {t(msg`Public URL slug`)}
                  <input
                    class={field}
                    data-testid="teacher-slug"
                    value={p()?.publicSlug ?? ""}
                    onChange={(e) =>
                      patch({ publicSlug: slugifyPublicSlug(e.currentTarget.value) || null })
                    }
                  />
                </label>

                <div class="flex items-center justify-between rounded-xl border border-border p-3">
                  <div>
                    <p class="text-sm font-semibold">{t(msg`Public profile`)}</p>
                    <p class="text-xs text-muted-foreground">
                      {t(msg`Anyone with the link can view this page.`)}
                    </p>
                  </div>
                  <Switch
                    checked={p()?.isPublic === true}
                    onCheckedChange={(v) => patch({ isPublic: v })}
                    data-testid="teacher-is-public"
                  />
                </div>

                <Show when={p()?.isPublic === true && publicUrl()}>
                  <a
                    href={publicUrl()}
                    target="_blank"
                    rel="noreferrer"
                    class="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
                    data-testid="teacher-public-link"
                  >
                    <ExternalLink class="h-4 w-4" /> {t(msg`Open public page`)}
                  </a>
                </Show>

                <Show when={canEdit()}>
                  <div class="flex items-center gap-2 pt-1 text-xs text-muted-foreground">
                    <Save class="h-3.5 w-3.5" /> {t(msg`Changes save automatically.`)}
                  </div>
                </Show>
              </fieldset>

              {/* Vista previa en vivo */}
              <div class="space-y-2">
                <p class="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  {t(msg`Live preview`)}
                </p>
                <div
                  class="rounded-2xl border border-border bg-muted/20 p-4"
                  data-testid="teacher-preview"
                >
                  <PublicTeacherContent page={previewPage()} preview />
                </div>
              </div>
            </div>
          </Show>
        </Show>
      </div>
    </PageContainer>
  );
};

/** Query local de la fila `organization` (para slug/nombre/logo en el preview). */
function useOrganizationRow(orgId: () => string | undefined) {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  type OrgRow = { name: string | null; slug: string | null; logo: string | null };
  return createPowerSyncSingle<OrgRow>({
    queryKey: () => academyPeopleQueryKeys.orgRow(orgId()),
    db,
    queryFn: () => {
      const d = drizzle();
      const id = orgId();
      return d && id
        ? d
            .select({ name: organization.name, slug: organization.slug, logo: organization.logo })
            .from(organization)
            .where(eq(organization.id, id))
            .limit(1)
        : null;
    },
    enabled: () => Boolean(orgId()) && Boolean(drizzle()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    suspense: false,
  });
}

/**
 * Especialidades como array de strings.
 *
 * E4: el DTO ya las trae tipadas (`jsonb` → array), así que el `JSON.parse` con
 * fallback a «separadas por comas» que hacía falta cuando SQLite las guardaba
 * como texto se ha ido. Queda el filtro defensivo por si el `jsonb` de una fila
 * vieja lleva algo que no es una cadena.
 */
function specialtiesOf(raw: string[] | null | undefined): string[] {
  return Array.isArray(raw) ? raw.filter((s): s is string => typeof s === "string") : [];
}
